import { useEffect, useRef } from 'react'

import type { RecruitmentProgress as Progress } from '../types'
import styles from './styles.module.scss'

type Props = {
  progress: Progress
  activeAreaCount: number
  prefectureCount: number
}

const RecruitmentProgress = ({
  progress,
  activeAreaCount,
  prefectureCount,
}: Props): JSX.Element => {
  const goalMeterRef = useRef<HTMLDivElement>(null)
  const percentageRef = useRef<HTMLSpanElement>(null)
  const fillRef = useRef<HTMLDivElement>(null)
  const statsRef = useRef<HTMLDivElement>(null)
  const activeAreaRef = useRef<HTMLElement>(null)
  const prefecturesRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const goalMeter = goalMeterRef.current
    const percentage = percentageRef.current
    const fill = fillRef.current
    const stats = statsRef.current
    const activeArea = activeAreaRef.current
    const prefectures = prefecturesRef.current
    if (!goalMeter || !percentage || !fill || !stats || !activeArea || !prefectures) return

    const format = (value: number) => value.toLocaleString('ja-JP')
    const setFinalValues = () => {
      percentage.textContent = format(progress.percentage)
      activeArea.textContent = format(activeAreaCount)
      prefectures.textContent = format(prefectureCount)
      fill.style.width = `${progress.percentage}%`
    }
    if (
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      typeof IntersectionObserver === 'undefined'
    ) {
      setFinalValues()
      return
    }

    const animationFrames = new Set<number>()
    const countUp = (element: HTMLElement, target: number) => {
      const start = performance.now()
      const tick = (now: number) => {
        const ratio = Math.min(1, (now - start) / 1100)
        element.textContent = format(Math.round(target * (1 - Math.pow(1 - ratio, 3))))
        if (ratio < 1) animationFrames.add(requestAnimationFrame(tick))
      }
      animationFrames.add(requestAnimationFrame(tick))
    }
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          observer.unobserve(entry.target)
          if (entry.target === goalMeter) {
            countUp(percentage, progress.percentage)
            requestAnimationFrame(() => {
              fill.style.width = `${progress.percentage}%`
            })
          }
          if (entry.target === stats) {
            countUp(activeArea, activeAreaCount)
            countUp(prefectures, prefectureCount)
          }
        }),
      { threshold: 0.12 }
    )
    observer.observe(goalMeter)
    observer.observe(stats)
    return () => {
      observer.disconnect()
      animationFrames.forEach(cancelAnimationFrame)
    }
  }, [activeAreaCount, prefectureCount, progress.percentage])

  return (
    <>
      <div className={`${styles.goalMeter} ${styles.reveal}`} data-reveal="" ref={goalMeterRef}>
        <div className={styles.goalHead}>
          <div className={styles.goalLead}>
            <img src="/partner-introduction/pin.png" alt="" />
            日本地図は、いまここまで埋まっています
          </div>
          <div className={styles.goalPercentage}>
            <span ref={percentageRef}>0</span>
            <i>%</i>
          </div>
        </div>
        <div className={styles.goalTrack} aria-hidden="true">
          <div className={styles.goalFill} ref={fillRef} style={{ width: 0 }}>
            <img src="/partner-introduction/pin.png" alt="" />
          </div>
        </div>
      </div>
      <div className={`${styles.mapStats} ${styles.reveal}`} data-reveal="" ref={statsRef}>
        <div className={styles.stat}>
          <b ref={activeAreaRef}>0</b>
          <span>募集中の駅・施設</span>
        </div>
        <div className={styles.stat}>
          <b ref={prefecturesRef}>0</b>
          <span>都道府県で展開中</span>
        </div>
      </div>
    </>
  )
}

export default RecruitmentProgress
